import React from 'react'
import './localWebAwesome.css'

type VariantProps = {
  appearance?: string
  size?: string
  variant?: string
}

const classes = (...values: Array<string | false | undefined>): string =>
  values.filter(Boolean).join(' ')

type ButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'size'> &
  VariantProps & {
    circle?: boolean
    href?: string
    loading?: boolean
    pill?: boolean
    slot?: string
    target?: string
    rel?: string
  }

/**
 * Same-origin replacements for the Web Awesome components used by the shared
 * devnw packages. They deliberately render semantic native elements: this
 * keeps keyboard behavior intact while eliminating Web Awesome's custom
 * element loader and default Font Awesome CDN resolver from the runtime.
 */
export const WaButton = React.forwardRef<HTMLElement, ButtonProps>(
  (
    {
      appearance = 'filled',
      children,
      circle,
      className,
      disabled,
      href,
      loading,
      pill,
      rel,
      size = 'medium',
      slot,
      target,
      type = 'button',
      variant = 'neutral',
      ...rest
    },
    ref
  ) => {
    const classNames = classes(
      'local-wa-button',
      className,
      circle && 'local-wa-button--circle',
      pill && 'local-wa-button--pill'
    )
    const dataProps = {
      'data-appearance': appearance,
      'data-size': size,
      'data-variant': variant,
    }

    if (href && !disabled) {
      const anchorRest = { ...rest }
      delete anchorRest.form
      delete anchorRest.formAction
      delete anchorRest.formEncType
      delete anchorRest.formMethod
      delete anchorRest.formNoValidate
      delete anchorRest.formTarget
      delete anchorRest.value
      return (
        <a
          {...(anchorRest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
          {...dataProps}
          ref={ref as React.Ref<HTMLAnchorElement>}
          className={classNames}
          data-slot={slot}
          href={href}
          rel={rel}
          target={target}
        >
          {loading ? <WaSpinner aria-label="Loading" /> : children}
        </a>
      )
    }

    return (
      <button
        {...rest}
        {...dataProps}
        ref={ref as React.Ref<HTMLButtonElement>}
        aria-busy={loading || undefined}
        className={classNames}
        data-slot={slot}
        disabled={disabled || loading}
        type={type}
      >
        {loading ? <WaSpinner aria-label="Loading" /> : children}
      </button>
    )
  }
)
WaButton.displayName = 'LocalWaButton'

type BadgeProps = React.HTMLAttributes<HTMLSpanElement> &
  VariantProps & {
    href?: string
    pill?: boolean
    rel?: string
    target?: string
  }

export const WaBadge = ({
  appearance,
  children,
  className,
  href,
  pill,
  rel,
  size,
  target,
  variant = 'neutral',
  ...rest
}: BadgeProps): JSX.Element => {
  const classNames = classes(
    'local-wa-badge',
    className,
    pill && 'local-wa-badge--pill'
  )
  const dataProps = {
    'data-appearance': appearance,
    'data-size': size,
    'data-variant': variant,
  }
  if (href) {
    return (
      <a
        {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
        {...dataProps}
        className={classNames}
        href={href}
        rel={rel}
        target={target}
      >
        {children}
      </a>
    )
  }
  return (
    <span {...rest} {...dataProps} className={classNames}>
      {children}
    </span>
  )
}

type BoxProps = React.HTMLAttributes<HTMLDivElement> & VariantProps

export const WaCard = ({
  appearance,
  children,
  className,
  size,
  variant,
  ...rest
}: BoxProps): JSX.Element => (
  <div
    {...rest}
    className={classes('local-wa-card', className)}
    data-appearance={appearance}
    data-size={size}
    data-variant={variant}
  >
    {children}
  </div>
)

export const WaTag = ({
  appearance,
  children,
  className,
  size,
  variant = 'neutral',
  ...rest
}: React.HTMLAttributes<HTMLSpanElement> & VariantProps): JSX.Element => (
  <span
    {...rest}
    className={classes('local-wa-tag', className)}
    data-appearance={appearance}
    data-size={size}
    data-variant={variant}
  >
    {children}
  </span>
)

export const WaDivider = ({
  className,
  ...rest
}: React.HTMLAttributes<HTMLHRElement>): JSX.Element => (
  <hr {...rest} className={classes('local-wa-divider', className)} />
)

export const WaSpinner = ({
  className,
  ...rest
}: React.HTMLAttributes<HTMLSpanElement>): JSX.Element => (
  <span
    {...rest}
    className={classes('local-wa-spinner', className)}
    role={rest.role ?? 'status'}
  />
)

type DropdownProps = React.DetailsHTMLAttributes<HTMLDetailsElement> & {
  hoist?: boolean
  onWaHide?: (event: React.SyntheticEvent<HTMLDetailsElement>) => void
  onWaShow?: (event: React.SyntheticEvent<HTMLDetailsElement>) => void
  placement?: string
}

export const WaDropdown = ({
  children,
  className,
  hoist,
  onToggle,
  onWaHide,
  onWaShow,
  placement = 'bottom-start',
  ...rest
}: DropdownProps): JSX.Element => {
  const entries = React.Children.toArray(children)
  const triggerIndex = entries.findIndex(
    (child) =>
      React.isValidElement<{ slot?: string }>(child) &&
      child.props.slot === 'trigger'
  )
  const trigger = triggerIndex >= 0 ? entries[triggerIndex] : 'Menu'
  const content = entries.filter((_, index) => index !== triggerIndex)

  return (
    <details
      {...rest}
      className={classes('local-wa-dropdown', className)}
      data-hoist={hoist || undefined}
      data-placement={placement}
      onToggle={(event) => {
        onToggle?.(event)
        if (event.currentTarget.open) onWaShow?.(event)
        else onWaHide?.(event)
      }}
    >
      <summary>{trigger}</summary>
      <div className="local-wa-dropdown__content">{content}</div>
    </details>
  )
}

type DropdownItemProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  onWaSelect?: (event: React.MouseEvent<HTMLButtonElement>) => void
}

export const WaDropdownItem = ({
  children,
  className,
  onClick,
  onWaSelect,
  type = 'button',
  ...rest
}: DropdownItemProps): JSX.Element => (
  <button
    {...rest}
    className={classes('local-wa-dropdown__item', className)}
    onClick={(event) => {
      onWaSelect?.(event)
      if (!event.defaultPrevented) onClick?.(event)
    }}
    type={type}
  >
    {children}
  </button>
)
